import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, computed, effect, input, signal, untracked } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-zoom-container',
  imports: [CommonModule, TranslocoModule, Button],
  templateUrl: './zoom-container.component.html',
  styleUrl: './zoom-container.component.scss',
  standalone: true,
})
export class ZoomContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  initialScale = input<number>(1);
  minHeight = input<string>('none');
  maxHeight = input<string>('none');
  @Input() minScale = 1;
  @Input() maxScale = 5;
  @Input() zoomSensitivity = 0.2;
  @Input() showHint = false;

  @ViewChild('zoomElement', { static: false }) zoomElement!: ElementRef<HTMLDivElement>;

  deviceHasTouch = false;
  scale = signal(1);
  translateX = signal(0);
  translateY = signal(0);
  isDragging = false;
  isScaled = false;
  lastMouseX = 0;
  lastMouseY = 0;
  mouseDownX = 0;
  mouseDownY = 0;
  width = 0;
  height = 0;
  private _onGoingAnimationFrame?: number;

  transform = computed(() => `translate(${this.translateX()}px, ${this.translateY()}px) scale(${this.scale()})`);

  adjustedMinHeight = computed(() => {
    const shouldNotBeInitiallyScaled = this.deviceHasTouch || document.body.clientWidth < 1024;
    return shouldNotBeInitiallyScaled ? undefined : this.minHeight();
  });

  initialScaleEffect = effect(() => {
    const initialScale = this.initialScale();

    untracked(() => {
      const shouldNotBeInitiallyScaled = this.deviceHasTouch || document.body.clientWidth < 1024;
      const newInitialScale = shouldNotBeInitiallyScaled ? 1 : initialScale;
      this.scale.set(newInitialScale);
      this.isScaled = newInitialScale !== 1;
    });
  });

  ngOnInit() {
    this.deviceHasTouch = 'ontouchstart' in document.documentElement;
  }

  ngAfterViewInit(): void {
    this.zoomElement.nativeElement.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
    this.zoomElement.nativeElement.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.zoomElement.nativeElement.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.zoomElement.nativeElement.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.zoomElement.nativeElement.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.zoomElement.nativeElement.addEventListener('dragstart', (event) => event.preventDefault());
    this.zoomElement.nativeElement.addEventListener('dblclick', this.reset.bind(this));
  }

  ngOnDestroy(): void {
    if (this._onGoingAnimationFrame) {
      cancelAnimationFrame(this._onGoingAnimationFrame);
    }
    this.zoomElement.nativeElement.removeEventListener('wheel', this.onWheel.bind(this));
    this.zoomElement.nativeElement.removeEventListener('mousedown', this.onMouseDown.bind(this));
    this.zoomElement.nativeElement.removeEventListener('mousemove', this.onMouseMove.bind(this));
    this.zoomElement.nativeElement.removeEventListener('mouseup', this.onMouseUp.bind(this));
    this.zoomElement.nativeElement.removeEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.zoomElement.nativeElement.removeEventListener('dblclick', this.reset.bind(this));
  }

  closeHint(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.showHint = false;
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();

    if (this.deviceHasTouch) return;

    const rect = this._getBoundingRect(event);
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const didZoomOut = event.deltaY > 0;
    const zoomFactor = didZoomOut ? 1 - this.zoomSensitivity : 1 + this.zoomSensitivity;
    const newScale = this.scale() * zoomFactor;
    this._zoom(newScale, mouseX, mouseY, rect);
  }

  onMouseDown(event: MouseEvent) {
    if (this.deviceHasTouch) return;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
    this.mouseDownX = event.clientX;
    this.mouseDownY = event.clientY;
    if (this.scale() >= this.minScale) {
      this.isDragging = true;
    }
  }

  onMouseMove(event: MouseEvent) {
    if (this.deviceHasTouch) return;
    if (!this.isDragging) return;

    // Throttle with requestAnimationFrame (max 60fps)
    if (this._onGoingAnimationFrame) return;

    this._onGoingAnimationFrame = requestAnimationFrame(() => {
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;
      const newX = this.translateX() + deltaX;
      const newY = this.translateY() + deltaY;
      this._setConstrainedValues(newX, newY);
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this._onGoingAnimationFrame = undefined;
    });
  }

  onMouseUp(event: MouseEvent) {
    if (this.deviceHasTouch) return;
    const wasMoved = event.clientX !== this.mouseDownX || event.clientY !== this.mouseDownY;
    const wasDoubleClick = event.detail === 2;
    if (this.isScaled && wasDoubleClick && !wasMoved) {
      this.reset();
    }
    this.isDragging = false;
  }

  onMouseLeave() {
    if (this.deviceHasTouch) return;
    this.isDragging = false;
  }

  reset() {
    this.scale.set(this.initialScale());
    this.translateX.set(0);
    this.translateY.set(0);
    this.isScaled = false;
    this.isDragging = false;
  }

  private _getBoundingRect(event?: Event): DOMRect {
    let result: DOMRect;
    if (event) {
      result = (event.currentTarget as Element).getBoundingClientRect();
    } else if (this.zoomElement) {
      result = this.zoomElement.nativeElement.getBoundingClientRect();
    } else {
      console.warn('[ZoomContainerComponent] getBoundingRect() - no event or zoomElement available');
      result = new DOMRect();
    }
    return result;
  }

  private _setConstrainedValues(x: number, y: number, rect?: DOMRect) {
    const localRect = rect || this._getBoundingRect();
    this.width = localRect.width;
    this.height = localRect.height;

    if (this.scale() < 1) {
      const maxTranslateX = this.width * this.scale() + (this.width / 2) * this.scale();
      const minTranslateX = 0 - (this.width * this.scale()) / 2;
      if (x > maxTranslateX) x = maxTranslateX;
      if (x < minTranslateX) x = minTranslateX;
      const maxTranslateY = this.height * this.scale() + (this.height / 2) * this.scale();
      const minTranslateY = 0 - (this.height * this.scale()) / 2;
      if (y > maxTranslateY) y = maxTranslateY;
      if (y < minTranslateY) y = minTranslateY;
    } else {
      const moveGapInPercent = 20;
      const moveGap = (this.width * moveGapInPercent) / 100;
      if (x > moveGap) x = moveGap;
      if (y > moveGap) y = moveGap;
      const maxTranslateX = this.width - this.width * this.scale() - moveGap;
      if (x < maxTranslateX) x = maxTranslateX;
      const maxTranslateY = this.height - this.height * this.scale() - moveGap;
      if (y < maxTranslateY) y = maxTranslateY;
    }

    this.translateX.set(x);
    this.translateY.set(y);
  }

  private _zoom(newScale: number, mouseX: number, mouseY: number, rect: DOMRect) {
    const oldScale = this.scale();
    newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

    if (newScale === oldScale) return;

    this.width = rect.width;
    this.height = rect.height;

    let newTranslateX = 0;
    let newTranslateY = 0;

    if (newScale < 1) {
      // center the image
      newTranslateX = (this.width - this.width * newScale) / 2;
      newTranslateY = (this.height - this.height * newScale) / 2;
    } else {
      newTranslateX = mouseX - (newScale / oldScale) * (mouseX - this.translateX());
      newTranslateY = mouseY - (newScale / oldScale) * (mouseY - this.translateY());
    }
    this.scale.set(newScale);

    this._setConstrainedValues(newTranslateX, newTranslateY, rect);
    this.isScaled = this.scale() !== 1;
  }
}
